import React from 'react';
import styled, { css } from 'styled-components';
import { Paper, PaperProps } from '../paper';

interface TableWrapperInnerProps {
  autoColumn?: number;
  columns?: Array<number | null>;
  simpleAutoColumn?: boolean;
}

const TableWrapperBase = styled(Paper)`
  display: grid;
  grid-template-rows: 100%;
  align-content: flex-start;
  justify-content: stretch;
  justify-items: center;

  &.MuiPaper-root {
    padding: 0;
  }
`;

export const TableFilters = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: start;
`;

export const TableWrapperInner = styled.div<
  TableWrapperInnerProps & { autoColumn: number; gridTemplateRows: string }
>`
  display: grid;
  grid-template-rows: ${(props) => props.gridTemplateRows};
  width: 100%;
  height: 100%;
  padding: 2.8rem;
  overflow: overlay;

  & > table {
    min-width: max-content;
  }

  & ${TableFilters} {
    grid-column-gap: 2.4rem;
    margin-left: 2.4rem;

    grid-auto-columns: ${(props) => props.autoColumn}rem;
    grid-template-columns: ${(props) =>
      props.columns
        ? props.columns.map((col, i) => (i == 0 ? `${(col || 0) - 1.2}rem` : `${col}rem`)).join(' ')
        : `${props.autoColumn - 1.2}rem`};

    ${(props) =>
      props.simpleAutoColumn &&
      css`
        grid-auto-columns: minmax(10rem, 20rem);
        grid-template-columns: initial;
      `}
  }

  & thead {
    ${(props) =>
      !props.simpleAutoColumn &&
      css`
        & > tr > * {
          width: ${props.autoColumn + 2.4}rem;
        }

        ${props.columns &&
        props.columns.map(
          (col, i) => css`
            & > tr > *:nth-of-type(${i + 1}) {
              width: ${(col || 0) + 2.4}rem;
            }
          `,
        )}

        & > tr > *:nth-last-of-type(1) {
          min-width: ${props.autoColumn + 2.4}rem;
          width: fit-content;
        }
      `}

    ${(props) =>
      props.simpleAutoColumn &&
      css`
        ${props.columns &&
        props.columns.map(
          (col, i) =>
            typeof col === 'number' &&
            css`
              & > tr > *:nth-of-type(${i + 1}) {
                width: ${(col || 0) + 2.4}rem;
              }
            `,
        )}
      `}
  }

  & .MuiTableCell-root {
    position: relative;
  }
`;

export const TableWrapperHeader = styled.div<{ align?: 'center' | 'right' }>`
  display: grid;
  grid-template-columns: 1fr max-content;
  align-content: flex-start;
  align-items: center;
`;

export const TableWrapperFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content;
  align-content: flex-end;
  align-items: center;

  margin-top: 2.8rem;
`;

export const TableWrapper: React.FC<PaperProps & TableWrapperInnerProps> = ({
  autoColumn = 20,
  simpleAutoColumn,
  columns,
  children,
  ...props
}: PaperProps & TableWrapperInnerProps) => {
  const childrenArray = React.Children.toArray(children);
  const count = childrenArray.length;
  const gridTemplateRows = count > 1 ? `${'max-content '.repeat(count - 1)}1fr` : 'max-content';

  return (
    <TableWrapperBase paperSize={'full-size'} {...props}>
      <TableWrapperInner
        autoColumn={autoColumn}
        simpleAutoColumn={simpleAutoColumn}
        columns={columns}
        gridTemplateRows={gridTemplateRows}
      >
        {children}
      </TableWrapperInner>
    </TableWrapperBase>
  );
};
